import React from 'react';
import { motion } from 'framer-motion';
import { ActivityIcon, BookOpenIcon, CheckCircle2Icon, CheckIcon, CreditCardIcon, CpuIcon, FactoryIcon, FileTextIcon, GaugeIcon, GiftIcon, GraduationCapIcon, HardHatIcon, LockIcon, NetworkIcon, RadioTowerIcon, ShieldCheckIcon, ShieldIcon, StarIcon, UsersIcon, WrenchIcon, XIcon, ZapIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Countdown } from './ui/Countdown';
import { FAQ } from './FAQ';
import { Footer } from './Footer';
import { PurchaseNotification } from './PurchaseNotification';
const productImage = 'https://cdn.magicpatterns.com/uploads/fWNXQipzbyWUzngTPxX5XX/ChatGPT_Image_14_jul_2026,_16_52_15.png';
const fadeUp = (delay = 0) => ({
  initial: {
    opacity: 0,
    y: 18
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    amount: 0.12
  },
  transition: {
    duration: 0.45,
    delay
  }
});
type Module = [string, string, string, React.ElementType, string];
const testimonials = [['CR', 'Carlos R.', 'Docente de Instrumentación Industrial · Neuquén', 'En una tarde tenía más material sobre instrumentación contextualizado en Argentina que en toda mi carrera docente. Ahora mis alumnos preguntan sobre Vaca Muerta, sensores de litio y una sala SCADA real, y tengo respuesta fundamentada para todo.'], ['MT', 'Martín T.', 'Docente de Automatización · Santa Cruz', 'Mis alumnos por primera vez entendieron la diferencia entre un sensor analógico y uno inteligente, y analizaron por qué Argentina necesita técnicos instrumentistas con formación actualizada. El nivel de participación se multiplicó.'], ['LP', 'Laura P.', 'Profesora de Control de Procesos · Córdoba', 'Los ejercicios de calibración de transmisores y ajuste de parámetros PID con datos de plantas argentinas son exactamente lo que mis alumnos necesitaban: ingeniería técnica real con contexto nacional. Cambió todo.']];
const modules: Module[] = [['01', 'Fundamentos de Instrumentación Industrial', 'Variables de proceso, señales analógicas y digitales, errores de medición, unidades, conversiones y normas ISA, IEC e IRAM para arrancar con autoridad.', GaugeIcon, 'VARIABLES · 4–20 mA · ISA · LAZO DE CONTROL · CALIBRACIÓN · INTI'], ['02', 'Sensores e Instrumentos de Medición', 'PT100, termopares, presión, nivel, caudal, humedad y proximidad. Criterios de selección, grados IP, ATEX y equipos usados en industrias argentinas.', RadioTowerIcon, 'PT100 · TERMOPAR · RADAR FMCW · CORIOLIS · HART · ATEX'], ['03', 'Control de Procesos Industriales', 'Lazos abiertos y cerrados, ON/OFF, PI y PID, válvulas, VFD, PLC, DCS, SCADA y HMI con casos reales de ajuste de procesos.', ActivityIcon, 'PID · ZIEGLER-NICHOLS · PLC · DCS · SCADA · HMI · PROFIBUS'], ['04', 'Instrumentación Aplicada a la Industria', 'Petróleo y gas, minería del litio, energía, alimentos, farmacéutica y agua. Casos de aplicación en los sectores clave de Argentina.', FactoryIcon, 'VACA MUERTA · LITIO · ATUCHA · AySA · GMP · MEDICIÓN FISCAL'], ['05', 'Diagramas, Simbología e Interpretación', 'Norma ISA-5.1, tags, P&ID, PFD, diagramas de lazo, matrices causa-efecto y lectura de planos industriales reales.', FileTextIcon, 'ISA-5.1 · P&ID · DIAGRAMA DE LAZO · TAG ISA · FC/FO'], ['06', 'Recursos Didácticos para Docentes', 'Planificación anual, secuencias por unidad, proyectos, guías de taller, rúbricas y evaluación alineada con el marco INET 2026.', GraduationCapIcon, 'ABP · RÚBRICAS · INET · COMPETENCIAS · EVALUACIÓN · 2026']];
const checklist = [['Contenido 100% argentino', 'Vaca Muerta, AySA, Nucleoeléctrica, litio jujeño, YPF, INTI e INET.'], ['Material imprimible', 'Listo para usar en el aula o entregar al alumno.'], ['Fichas de referencia rápida', 'Para consultar mientras enseñás.'], ['Ejercicios resueltos paso a paso', 'Con datos reales de plantas y empresas argentinas.'], ['Vocabulario técnico explicado', 'Con rigor técnico y claridad para el nivel del aula.'], ['Conexión con la actualidad', 'Vaca Muerta 2026, litio, agua potable y energía nuclear.'], ['Contexto industrial argentino', 'De las plantas de AySA a los pozos de Neuquén.'], ['4 bonos estratégicos', 'Actividades, evaluaciones, material visual y prácticas de laboratorio.'], ['Garantía de 7 días', 'Sin preguntas ni formularios.'], ['Acceso y actualizaciones', 'Descarga inmediata y acceso de por vida al material.']];
const bonuses = [['01', 'Banco de 100 Actividades para Docentes', 'Crucigramas, problemas numéricos, casos industriales argentinos y actividades colaborativas organizadas por unidad, dificultad y tiempo disponible.', '$18.000'], ['02', 'Banco Completo de Evaluaciones con Solucionario', 'Evaluaciones diagnósticas, formativas y sumativas, preguntas de desarrollo, opción múltiple, ejercicios resueltos y casos reales.', '$15.000'], ['03', 'Presentaciones y Material Visual para Clase', 'Seis presentaciones listas para proyectar, infografías, diagramas de procesos, esquemas técnicos y tablas comparativas.', '$15.000'], ['04', 'Manual de Prácticas de Laboratorio y Taller', '20 prácticas guiadas: PT100, calibración de transmisores, lazo 4–20 mA, diagnóstico de fallas y verificación HART.', '$20.000']];
const comparison = [['Preparar clases técnicas avanzadas', 'Buscás horas sin encontrar nivel suficiente', 'Sensores inteligentes, IIoT, HART 7 y WirelessHART listos para usar'], ['Explicar Industria 4.0 en profundidad', 'Nociones básicas sin análisis completo', 'Arquitectura IIoT, OPC-UA, MQTT, gemelos digitales y casos argentinos'], ['Hablar de redes industriales', 'Explicás lo básico sin protocolos reales', 'Modbus, Profibus, Foundation Fieldbus y Ethernet Industrial'], ['Responder sobre mantenimiento predictivo', 'Datos parciales sin profundidad técnica', 'Vibración, CMS, ROI y casos de Vaca Muerta y Jujuy'], ['Proyectar el futuro de la instrumentación', 'Sin base para hablar de IIoT y gemelos digitales', 'Hoja de ruta 2026–2030 y tres proyectos integradores 4.0']];
const premiumModules: Array<[string, string, React.ElementType]> = [['Instrumentación Inteligente y Sensores Avanzados', 'Microprocesadores en campo, compensación automática, autodiagnóstico NAMUR NE107, sensores multivariables y Edge Computing.', CpuIcon], ['IIoT y Redes Industriales', 'Arquitectura campo-edge-cloud, OPC-UA, MQTT, HART, Modbus, Profibus, Ethernet Industrial y WirelessHART con ejercicios prácticos.', NetworkIcon], ['Mantenimiento Predictivo y Diagnóstico Remoto', 'Vibración, firmas de falla, sistemas CMS, Asset Management y el ROI de prevenir paradas no programadas.', WrenchIcon], ['Gemelos Digitales y Tendencias 2026–2030', 'Digital twins para industria, nuevos roles técnicos y tres proyectos con Node-RED, Grafana y MQTT.', FactoryIcon]];
function scrollToPricing() {
  document.getElementById('pricing')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function CheckLine({
  children,
  gold = false
}: {
  children: React.ReactNode;
  gold?: boolean;
}) {
  return <li className="flex items-start gap-3 text-sm leading-snug text-forest" data-id="element-22">
      <CheckIcon className={`mt-0.5 h-5 w-5 shrink-0 ${gold ? 'text-gold' : 'text-earth'}`} data-id="element-23" />
      {children}
    </li>;
}
export function LandingPage() {
  return <div className="min-h-screen w-full overflow-hidden bg-base text-forest-dark" data-id="element-24">
      <main data-id="element-25">
        <section className="relative isolate overflow-hidden border-b border-sand/60 bg-base pb-16 pt-12 sm:pb-20 sm:pt-16" data-id="element-26">
          <div className="absolute inset-x-0 top-0 -z-10 h-64 bg-surface" data-id="element-27" />
          <div className="absolute right-0 top-0 -z-10 h-80 w-80 border-b border-l border-sand/30" data-id="element-28" />
          <div className="mx-auto max-w-6xl px-4" data-id="element-29">
            <div className="mx-auto max-w-4xl text-center" data-id="element-30">
              <motion.p {...fadeUp()} className="inline-flex items-center gap-2 rounded-full border border-sand bg-surface px-4 py-2 text-xs font-extrabold uppercase tracking-[.14em] text-earth" data-id="element-31">
                <FactoryIcon className="h-4 w-4 text-gold" data-id="element-32" /> Megapack
                Instrumentación Industrial Listo™
              </motion.p>
              <motion.h1 {...fadeUp(0.08)} className="mx-auto mt-5 max-w-4xl text-balance text-4xl font-extrabold leading-[1.04] text-forest-dark sm:text-6xl md:text-7xl" data-id="element-33">
                Del sensor al aula.
                <br data-id="element-34" />
                <span className="text-forest" data-id="element-35">Todo resuelto.</span>
              </motion.h1>
              <motion.div initial={{
              opacity: 0,
              scale: 0.94
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.22
            }} className="mx-auto my-5 max-w-xl sm:my-7" data-id="element-36">
                <img src={productImage} alt="Megapack Instrumentación Industrial Listo con seis tomos, bonos y módulo Industria 4.0" className="mx-auto w-full drop-shadow-[0_24px_28px_rgba(0,0,0,.48)]" data-id="element-37" />
              </motion.div>
              <div className="flex flex-wrap justify-center gap-2 text-xs font-bold text-forest" data-id="element-38">
                {['Acceso inmediato', 'Hecho para Argentina', 'Pago seguro'].map(item => <span key={item} className="rounded-full border border-sand bg-surface px-3 py-2" data-id="element-39">
                    {item}
                  </span>)}
              </div>
              <div className="mt-7" data-id="element-40">
                <Button onClick={scrollToPricing} arrow data-id="element-41">
                  QUIERO MI MEGAPACK AHORA
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-semibold text-muted" data-id="element-42">
                <span className="inline-flex items-center gap-1.5" data-id="element-43">
                  <ShieldIcon className="h-4 w-4 text-earth" data-id="element-44" /> Garantía 7 días
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-45">
                  <LockIcon className="h-4 w-4 text-earth" data-id="element-46" /> Pago seguro
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-47">
                  <CreditCardIcon className="h-4 w-4 text-gold" data-id="element-48" /> Mercado Pago
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-t border-sand/60 bg-surface py-5" aria-label="Información del producto" data-id="element-49">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 text-sm font-semibold text-muted sm:justify-between" data-id="element-50">
            <span className="inline-flex items-center gap-2" data-id="element-51">
              <UsersIcon className="h-5 w-5 text-earth" data-id="element-52" /> +150 docentes
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-53">
              <BookOpenIcon className="h-5 w-5 text-earth" data-id="element-54" /> 6 tomos completos
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-55">
              <GiftIcon className="h-5 w-5 text-earth" data-id="element-56" /> 4 bonos incluidos
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-57">
              <StarIcon className="h-5 w-5 fill-gold text-gold" data-id="element-58" /> 4.9/5
              calificación
            </span>
          </div>
        </section>

        <section className="bg-base py-16 sm:py-20" aria-labelledby="reviews-title" data-id="element-59">
          <div className="mx-auto max-w-6xl px-4" data-id="element-60">
            <p className="text-center text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-61">
              Experiencias reales
            </p>
            <h2 id="reviews-title" className="mt-3 text-center text-3xl font-extrabold text-forest-dark sm:text-4xl" data-id="element-62">
              Lo que dicen quienes ya lo tienen
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-63">
              {testimonials.map(([initials, name, role, quote], index) => <motion.article key={name} {...fadeUp(index * 0.08)} className="flex min-h-[255px] flex-col rounded-2xl border border-sand bg-surface p-6 shadow-card" data-id="element-64">
                  <div className="flex text-gold" data-id="element-65">
                    {Array.from({
                  length: 5
                }).map((_, star) => <StarIcon key={star} className="h-4 w-4 fill-current" data-id="element-66" />)}
                  </div>
                  <blockquote className="mt-4 flex-1 text-sm italic leading-relaxed text-muted" data-id="element-67">
                    “{quote}”
                  </blockquote>
                  <div className="mt-5 flex items-center gap-3 border-t border-sand/60 pt-4" data-id="element-68">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-soft text-sm font-extrabold text-gold" data-id="element-69">
                      {initials}
                    </span>
                    <span data-id="element-70">
                      <b className="block text-sm text-forest-dark" data-id="element-71">{name}</b>
                      <small className="text-xs text-muted" data-id="element-72">{role}</small>
                    </span>
                  </div>
                </motion.article>)}
            </div>
          </div>
        </section>

        <section className="bg-surface py-16 sm:py-20" aria-labelledby="problem-title" data-id="element-73">
          <div className="mx-auto max-w-4xl px-4" data-id="element-74">
            <div className="text-center" data-id="element-75">
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-76">
                La realidad docente
              </p>
              <h2 id="problem-title" className="mx-auto mt-3 max-w-3xl text-balance text-3xl font-extrabold leading-tight sm:text-4xl" data-id="element-77">
                ¿Cuántas clases más vas a preparar sobre instrumentación
                industrial sin tener el material correcto?
              </h2>
              <p className="mx-auto mt-5 max-w-3xl leading-relaxed text-muted" data-id="element-78">
                Buscás información sin secuencia didáctica ni ejemplos
                argentinos. Y cuando surge Vaca Muerta, sensores en el litio
                jujeño, SCADA de AySA o Industria 4.0, te falta un recurso que
                conecte a tus alumnos con la realidad industrial del país.
              </p>
              <p className="mt-4 font-bold text-forest" data-id="element-79">
                No es falta de vocación docente. Es que nadie había armado el
                sistema completo para vos.
              </p>
            </div>
            <motion.div {...fadeUp(0.1)} className="mt-9 rounded-2xl border border-red-400/35 bg-red-950/30 p-6 sm:p-8" data-id="element-80">
              <p className="mb-5 font-serif text-xl font-bold text-red-200" data-id="element-81">
                Esto quizás te está pasando hoy:
              </p>
              <ul className="grid gap-4 sm:grid-cols-2" data-id="element-82">
                {['Perdés tiempo buscando material que casi nunca tiene ejemplos argentinos.', 'Tus alumnos se desconectan de ejemplos que no hablan de la industria local.', 'No podés conectar la instrumentación con Vaca Muerta, litio, agua potable y energía.', 'Tus evaluaciones se repiten por falta de ejercicios técnicos actualizados.', 'Llegás al ciclo lectivo sin una secuencia completa lista para usar.', 'Industria 4.0 e IIoT te encuentran sin datos técnicos reales para responder.'].map(item => <li key={item} className="flex gap-3 text-sm leading-relaxed text-forest" data-id="element-83">
                    <span className="mt-0.5 rounded-full bg-red-400/15 p-1" data-id="element-84">
                      <XIcon className="h-3.5 w-3.5 text-red-300" data-id="element-85" />
                    </span>
                    {item}
                  </li>)}
              </ul>
            </motion.div>
            <p className="mt-8 text-center font-bold text-earth" data-id="element-86">
              ¿Y si esta fuera la última semana que enseñás instrumentación
              industrial sin tener todo resuelto?
            </p>
          </div>
        </section>

        <section className="bg-base py-16 sm:py-20" aria-labelledby="tomos-title" data-id="element-87">
          <div className="mx-auto max-w-6xl px-4" data-id="element-88">
            <div className="text-center" data-id="element-89">
              <span className="inline-flex items-center gap-2 rounded-full border border-sand bg-surface px-4 py-2 text-xs font-extrabold uppercase tracking-[.15em] text-earth" data-id="element-90">
                <FactoryIcon className="h-4 w-4 text-gold" data-id="element-91" /> Los 6 tomos
              </span>
              <h2 id="tomos-title" className="mx-auto mt-4 max-w-3xl text-balance text-3xl font-extrabold sm:text-4xl" data-id="element-92">
                Un tomo por cada dimensión de la instrumentación industrial
                argentina
              </h2>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-93">
              {modules.map(([number, title, description, Icon, tags], index) => <motion.article key={title} {...fadeUp(index * 0.06)} className="flex flex-col rounded-2xl border border-sand bg-surface p-6 shadow-card" data-id="element-94">
                    <div className="flex items-start justify-between gap-4" data-id="element-95">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-forest-soft text-gold" data-id="element-96">
                        <Icon className="h-6 w-6" data-id="element-97" />
                      </span>
                      <span className="font-serif text-2xl font-bold text-sand" data-id="element-98">
                        Tomo {number}
                      </span>
                    </div>
                    <h3 className="mt-5 text-xl font-bold text-forest-dark" data-id="element-99">
                      {title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-100">
                      {description}
                    </p>
                    <p className="mt-5 border-t border-sand/60 pt-4 text-[10px] font-extrabold uppercase tracking-[.1em] text-earth" data-id="element-101">
                      {tags}
                    </p>
                  </motion.article>)}
            </div>
          </div>
        </section>

        <section className="border-y border-sand/60 bg-surface py-16" aria-labelledby="includes-title" data-id="element-102">
          <div className="mx-auto max-w-5xl px-4" data-id="element-103">
            <h2 id="includes-title" className="text-center text-3xl font-extrabold text-forest-dark" data-id="element-104">
              Todos los tomos incluyen
            </h2>
            <div className="mt-9 grid gap-x-10 gap-y-4 md:grid-cols-2" data-id="element-105">
              {checklist.map(([title, detail], index) => <motion.div key={title} {...fadeUp(index * 0.03)} className="flex gap-3 rounded-xl p-2" data-id="element-106">
                  <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" data-id="element-107" />
                  <p data-id="element-108">
                    <b className="block text-sm text-forest-dark" data-id="element-109">{title}</b>
                    <span className="text-sm text-muted" data-id="element-110">{detail}</span>
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#050C15] py-16 text-forest-dark sm:py-20" aria-labelledby="bonos-title" data-id="element-111">
          <div className="absolute right-0 top-0 h-40 w-40 border-b border-l border-gold/45" data-id="element-112" />
          <div className="absolute bottom-0 left-0 h-36 w-36 border-r border-t border-sand/35" data-id="element-113" />
          <div className="relative mx-auto max-w-5xl px-4" data-id="element-114">
            <div className="text-center" data-id="element-115">
              <GiftIcon className="mx-auto h-10 w-10 text-gold" data-id="element-116" />
              <p className="mt-4 text-xs font-extrabold uppercase tracking-[.18em] text-gold" data-id="element-117">
                Siempre incluidos · siempre gratis
              </p>
              <h2 id="bonos-title" className="mt-3 text-3xl font-extrabold sm:text-4xl" data-id="element-118">
                4 bonos estratégicos para docentes
              </h2>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-119">
              {bonuses.map(([number, title, description, value], index) => <motion.article key={title} {...fadeUp(index * 0.06)} className="flex flex-col rounded-2xl border border-forest-soft bg-surface p-6" data-id="element-120">
                  <span className="mb-5 w-fit rounded-full bg-forest-soft px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-gold" data-id="element-121">
                    Bono {number}
                  </span>
                  <h3 className="text-xl font-bold" data-id="element-122">{title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted" data-id="element-123">
                    {description}
                  </p>
                  <p className="mt-5 border-t border-sand/50 pt-4 text-sm" data-id="element-124">
                    <span className="text-muted/60 line-through" data-id="element-125">
                      Valor {value}
                    </span>
                    <span className="float-right font-extrabold text-gold" data-id="element-126">
                      GRATIS
                    </span>
                  </p>
                </motion.article>)}
            </div>
            <p className="mx-auto mt-8 max-w-xl rounded-xl border border-sand/50 bg-surface px-5 py-4 text-center text-sm text-muted" data-id="element-127">
              Valor total de bonos{' '}
              <span className="mx-1 line-through text-muted/70" data-id="element-128">
                $68.000 ARS
              </span>{' '}
              <b className="text-gold" data-id="element-129">→ GRATIS con tu Megapack</b>
            </p>
          </div>
        </section>

        <section className="border-y-4 border-gold-dark bg-[#172534] py-12 text-center text-forest-dark" aria-label="Oferta por tiempo limitado" data-id="element-130">
          <div className="mx-auto max-w-3xl px-4" data-id="element-131">
            <p className="text-xs font-extrabold uppercase tracking-[.16em] text-gold" data-id="element-132">
              Precio de lanzamiento · cierra en
            </p>
            <h2 className="mt-2 font-serif text-2xl font-extrabold sm:text-3xl" data-id="element-133">
              Tu aula puede estar lista hoy
            </h2>
            <Countdown light className="mt-6 scale-110 shadow-lg" data-id="element-134" />
            <p className="mt-7 font-bold text-earth" data-id="element-135">
              Después sube. No vuelve al precio de lanzamiento.
            </p>
            <div className="mx-auto mt-6 max-w-xl rounded-xl border border-sand/50 bg-base p-4 text-sm" data-id="element-136">
              <p className="font-bold" data-id="element-137">
                <span className="mr-2 inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-gold" data-id="element-138" />
                Cupos al precio de lanzamiento: quedan pocos
              </p>
              <div className="mt-3 h-3 overflow-hidden rounded-full bg-[#02060B]" data-id="element-139">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '71%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1
              }} className="h-full rounded-full bg-gold" data-id="element-140" />
              </div>
              <p className="mt-1 text-right text-xs text-earth" data-id="element-141">71% tomados</p>
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-4 bg-base py-16 sm:py-24" aria-labelledby="pricing-title" data-id="element-142">
          <div className="mx-auto max-w-5xl px-4" data-id="element-143">
            <div className="text-center" data-id="element-144">
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-145">
                Acceso inmediato
              </p>
              <h2 id="pricing-title" className="mt-3 text-3xl font-extrabold sm:text-4xl" data-id="element-146">
                Elegí tu Megapack
              </h2>
              <p className="mt-3 text-muted" data-id="element-147">
                Dos opciones · Mismo acceso inmediato
              </p>
            </div>
            <div className="mt-12 grid items-stretch gap-7 lg:grid-cols-2" data-id="element-148">
              <article className="flex flex-col rounded-3xl border border-sand bg-surface p-6 shadow-card sm:p-8" data-id="element-149">
                <p className="text-xs font-extrabold uppercase tracking-[.15em] text-muted" data-id="element-150">
                  Opción 1
                </p>
                <h3 className="mt-3 text-2xl font-bold" data-id="element-151">
                  Megapack Instrumentación Industrial Listo™
                </h3>
                <p className="mt-2 font-bold text-earth" data-id="element-152">6 tomos + 4 bonos</p>
                <div className="mt-7" data-id="element-153">
                  <span className="text-sm text-muted line-through" data-id="element-154">
                    $72.000 ARS
                  </span>
                  <p className="mt-1 font-serif text-5xl font-extrabold text-forest-dark" data-id="element-155">
                    $19.990 <small className="font-sans text-base" data-id="element-156">ARS</small>
                  </p>
                </div>
                <ul className="mt-7 flex-1 space-y-3" data-id="element-157">
                  {['Tomo 1: Fundamentos de Instrumentación Industrial', 'Tomo 2: Sensores e Instrumentos de Medición', 'Tomo 3: Control de Procesos Industriales', 'Tomo 4: Instrumentación Aplicada a la Industria', 'Tomo 5: Diagramas, Simbología e Interpretación', 'Tomo 6: Recursos Didácticos para Docentes', '4 bonos estratégicos para docentes', 'Acceso de por vida y actualizaciones gratuitas'].map(item => <CheckLine key={item} data-id="element-158">{item}</CheckLine>)}
                </ul>
                <a href="https://www.centralpack.shop/cart/53905997267262:1" target="_blank" rel="noopener noreferrer" className="mt-8 block" data-id="element-159">
                  <Button variant="forest" arrow className="w-full" data-id="element-160">
                    QUIERO MI MEGAPACK
                  </Button>
                </a>
              </article>
              <article className="relative flex flex-col rounded-3xl border-2 border-gold bg-surface p-6 shadow-lift sm:p-8" data-id="element-161">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-4 py-2 text-xs font-extrabold text-base shadow-lg" data-id="element-162">
                  <StarIcon className="mr-1 inline h-3.5 w-3.5 fill-current" data-id="element-163" />{' '}
                  MÁS ELEGIDO
                </span>
                <p className="mt-3 text-xs font-extrabold uppercase tracking-[.15em] text-muted" data-id="element-164">
                  Opción 2
                </p>
                <h3 className="mt-3 text-2xl font-bold" data-id="element-165">
                  Megapack + Upsell Premium
                </h3>
                <p className="mt-2 font-bold text-gold" data-id="element-166">
                  6 tomos + 4 bonos + Industria 4.0 Avanzado
                </p>
                <div className="mt-7" data-id="element-167">
                  <span className="text-sm text-muted line-through" data-id="element-168">
                    $110.000 ARS
                  </span>
                  <p className="mt-1 font-serif text-5xl font-extrabold text-forest-dark" data-id="element-169">
                    $27.990 <small className="font-sans text-base" data-id="element-170">ARS</small>
                  </p>
                </div>
                <ul className="mt-7 flex-1 space-y-3" data-id="element-171">
                  <CheckLine gold data-id="element-172">
                    <b data-id="element-173">Todo el Megapack base, más:</b>
                  </CheckLine>
                  {['Instrumentación inteligente, sensores avanzados y Edge Computing', 'IIoT, HART 7, Modbus, Profibus y Ethernet Industrial', 'WirelessHART, LoRaWAN y casos de aplicación en Argentina', 'Mantenimiento predictivo, CMS y diagnóstico remoto', 'Gemelos digitales y tres proyectos con herramientas gratuitas', 'Acceso de por vida y actualizaciones gratuitas'].map(item => <CheckLine key={item} gold data-id="element-174">
                      <b className="text-forest" data-id="element-175">{item}</b>
                    </CheckLine>)}
                </ul>
                <a href="https://www.centralpack.shop/cart/53905999855934:1" target="_blank" rel="noopener noreferrer" className="mt-8 block" data-id="element-176">
                  <Button arrow className="w-full" data-id="element-177">
                    QUIERO EL MEGAPACK PREMIUM
                  </Button>
                </a>
                <p className="mt-4 text-center text-xs text-muted" data-id="element-178">
                  Tarjeta, transferencia o Mercado Pago en pesos argentinos.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="bg-surface py-16 sm:py-20" aria-labelledby="premium-title" data-id="element-179">
          <div className="mx-auto max-w-5xl px-4" data-id="element-180">
            <div className="text-center" data-id="element-181">
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-earth" data-id="element-182">
                La diferencia premium
              </p>
              <h2 id="premium-title" className="mt-3 text-3xl font-extrabold sm:text-4xl" data-id="element-183">
                ¿Qué agrega el Upsell Premium?
              </h2>
            </div>
            <div className="mt-10 overflow-x-auto pb-3" data-id="element-184">
              <div className="min-w-[700px]" data-id="element-185">
                <div className="grid grid-cols-3 gap-4 px-4 text-xs font-extrabold uppercase tracking-wider" data-id="element-186">
                  <p className="text-muted" data-id="element-187">Situación</p>
                  <p className="text-red-300" data-id="element-188">Sin Premium</p>
                  <p className="text-earth" data-id="element-189">Con Premium</p>
                </div>
                <div className="mt-3 space-y-2" data-id="element-190">
                  {comparison.map(([situation, without, withPremium]) => <div key={situation} className="grid grid-cols-3 gap-4 rounded-xl border border-sand bg-base p-4 text-sm" data-id="element-191">
                      <p className="font-bold text-forest-dark" data-id="element-192">{situation}</p>
                      <p className="text-muted" data-id="element-193">{without}</p>
                      <p className="rounded-lg border border-sand bg-forest-soft p-2 font-semibold text-forest" data-id="element-194">
                        {withPremium}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
            <p className="mt-1 text-center text-xs text-muted md:hidden" data-id="element-195">
              Deslizá para ver la tabla completa
            </p>
            <div className="mt-12 rounded-3xl border border-sand bg-base p-6 shadow-card sm:p-10" data-id="element-196">
              <h3 className="text-2xl font-bold text-forest-dark" data-id="element-197">
                Instrumentación inteligente e Industria 4.0 para enseñar con
                profundidad
              </h3>
              <div className="mt-7 grid gap-5 md:grid-cols-2" data-id="element-198">
                {premiumModules.map(([title, description, Icon], index) => <motion.article key={title} {...fadeUp(index * 0.06)} className="rounded-2xl border border-sand bg-surface p-6" data-id="element-199">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-forest-soft text-gold" data-id="element-200">
                      <Icon className="h-5 w-5" data-id="element-201" />
                    </span>
                    <h4 className="mt-4 text-lg font-bold" data-id="element-202">{title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-203">
                      {description}
                    </p>
                  </motion.article>)}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-sand/60 bg-base py-16 sm:py-20" aria-labelledby="guarantee-title" data-id="element-204">
          <div className="mx-auto max-w-2xl px-4 text-center" data-id="element-205">
            <motion.div {...fadeUp()} className="rounded-3xl border border-sand bg-surface p-8 shadow-card sm:p-12" data-id="element-206">
              <span className="inline-flex rounded-full border border-sand bg-forest-soft p-4 text-gold" data-id="element-207">
                <ShieldCheckIcon className="h-10 w-10" data-id="element-208" />
              </span>
              <h2 id="guarantee-title" className="mt-6 text-3xl font-extrabold" data-id="element-209">
                Garantía de 7 días, sin preguntas
              </h2>
              <p className="mt-4 leading-relaxed text-muted" data-id="element-210">
                Abrís los tomos. Los usás. Si en 7 días no es lo que esperabas,
                solicitás tu devolución.
              </p>
              <p className="mt-6 rounded-xl border border-sand bg-base px-5 py-4 text-lg font-extrabold text-forest" data-id="element-211">
                Devolución del 100% sin formularios, sin drama.
              </p>
              <p className="mt-5 text-xs font-bold uppercase tracking-[.14em] text-muted" data-id="element-212">
                Tasa de devoluciones: menor al 1%
              </p>
            </motion.div>
          </div>
        </section>

        <FAQ data-id="element-213" />

        <section className="bg-base py-16 sm:py-20" aria-labelledby="cost-title" data-id="element-214">
          <div className="mx-auto max-w-4xl px-4" data-id="element-215">
            <h2 id="cost-title" className="text-center text-3xl font-extrabold sm:text-4xl" data-id="element-216">
              El costo de no tenerlo
            </h2>
            <div className="mt-9 overflow-x-auto pb-3" data-id="element-217">
              <div className="min-w-[600px]" data-id="element-218">
                <div className="grid grid-cols-2 gap-4 px-4 text-xs font-extrabold uppercase tracking-wider" data-id="element-219">
                  <p className="text-muted" data-id="element-220">Sin el Megapack</p>
                  <p className="text-red-300" data-id="element-221">Lo que realmente perdés</p>
                </div>
                <div className="mt-3 space-y-2" data-id="element-222">
                  {[['Sin ejemplos argentinos', 'Alumnos desconectados de por qué la instrumentación importa en la Argentina real.'], ['Sin banco de actividades', 'Horas semanales perdidas armando material desde cero.'], ['Sin casos de estudio reales', 'Clases teóricas que no conectan con el mundo laboral argentino.'], ['Sin prácticas de laboratorio listas', 'Preparación que podría dedicarse a enseñar.'], ['Sin conexión con la actualidad', 'Alumnos que no ven la instrumentación detrás de Vaca Muerta, litio y agua potable.'], ['Sin planificaciones listas', 'Horas de diseño que podrían dedicarse a enseñar.']].map(([without, cost]) => <div key={without} className="grid grid-cols-2 gap-4 rounded-xl border border-sand bg-surface p-4 text-sm" data-id="element-223">
                      <p className="font-medium text-muted" data-id="element-224">{without}</p>
                      <p className="font-semibold text-red-200" data-id="element-225">{cost}</p>
                    </div>)}
                </div>
              </div>
            </div>
            <motion.blockquote {...fadeUp(0.1)} className="mt-10 rounded-2xl border border-sand bg-surface p-7 text-center shadow-card sm:p-10" data-id="element-226">
              <p className="font-serif text-xl font-bold leading-relaxed text-forest sm:text-2xl" data-id="element-227">
                “La próxima vez que un alumno pregunte para qué sirve estudiar
                instrumentación industrial, podés mostrarle que Argentina
                necesita técnicos para operar Vaca Muerta, monitorear el agua de
                millones de personas, controlar plantas de litio y sostener su
                matriz energética. O podés seguir explicando sin ese contexto.”
              </p>
              <HardHatIcon className="mx-auto mt-5 h-8 w-8 text-gold" data-id="element-228" />
            </motion.blockquote>
          </div>
        </section>

        <section className="border-t border-sand/60 bg-surface py-16 sm:py-20" aria-labelledby="final-title" data-id="element-229">
          <div className="mx-auto max-w-3xl px-4 text-center" data-id="element-230">
            <p className="text-xs font-extrabold uppercase tracking-[.16em] text-gold" data-id="element-231">
              El precio de lanzamiento cierra en
            </p>
            <Countdown className="mt-4" data-id="element-232" />
            <h2 id="final-title" className="mx-auto mt-8 max-w-2xl text-balance text-3xl font-extrabold sm:text-4xl" data-id="element-233">
              Elegí el recurso que devuelve tiempo y eleva tus clases técnicas
            </h2>
            <div className="mt-9 space-y-4 text-left" data-id="element-234">
              <div className="flex flex-col gap-4 rounded-2xl border border-sand bg-base p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-235">
                <p data-id="element-236">
                  <b className="block text-forest-dark" data-id="element-237">
                    MEGAPACK INSTRUMENTACIÓN INDUSTRIAL LISTO™
                  </b>
                  <span className="text-sm text-muted" data-id="element-238">6 tomos + 4 bonos</span>
                </p>
                <div className="sm:text-right" data-id="element-239">
                  <p data-id="element-240">
                    <span className="mr-2 text-sm text-muted line-through" data-id="element-241">
                      $72.000
                    </span>
                    <b data-id="element-242">$19.990 ARS</b>
                  </p>
                  <a href="https://www.centralpack.shop/cart/53905997267262:1" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block" data-id="element-243">
                    <Button size="sm" variant="outline" data-id="element-244">
                      ELEGIR MEGAPACK
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative flex flex-col gap-4 rounded-2xl border-2 border-gold bg-base p-5 shadow-card sm:flex-row sm:items-center sm:justify-between" data-id="element-245">
                <span className="absolute right-0 top-0 rounded-bl-lg bg-gold px-2 py-1 text-[10px] font-extrabold text-base" data-id="element-246">
                  MÁS ELEGIDO
                </span>
                <p data-id="element-247">
                  <b className="block text-forest-dark" data-id="element-248">
                    MEGAPACK + UPSELL PREMIUM
                  </b>
                  <span className="text-sm text-muted" data-id="element-249">
                    6 tomos + 4 bonos + Industria 4.0 Avanzado
                  </span>
                </p>
                <div className="sm:text-right" data-id="element-250">
                  <p data-id="element-251">
                    <span className="mr-2 text-sm text-muted line-through" data-id="element-252">
                      $110.000
                    </span>
                    <b data-id="element-253">$27.990 ARS</b>
                  </p>
                  <a href="https://www.centralpack.shop/cart/53905999855934:1" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block" data-id="element-254">
                    <Button size="sm" data-id="element-255">ELEGIR PREMIUM</Button>
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-semibold text-muted" data-id="element-256">
              <span className="inline-flex gap-1" data-id="element-257">
                <LockIcon className="h-4 w-4 text-earth" data-id="element-258" /> Pago 100% seguro
              </span>
              <span className="inline-flex gap-1" data-id="element-259">
                <ZapIcon className="h-4 w-4 text-gold" data-id="element-260" /> Acceso en 5 minutos
              </span>
              <span className="inline-flex gap-1" data-id="element-261">
                <ShieldIcon className="h-4 w-4 text-earth" data-id="element-262" /> Garantía 7 días
              </span>
              <span className="inline-flex gap-1" data-id="element-263">
                <CreditCardIcon className="h-4 w-4 text-gold" data-id="element-264" /> Mercado Pago ·
                Tarjeta · Transferencia
              </span>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-265" />
      <PurchaseNotification data-id="element-266" />
    </div>;
}